import _ from 'lodash';
import React from 'react';
import { fromJS } from 'immutable';
import { NavLink } from 'fluxible-router';
import InventoryRecord, { Badge } from '../../models/InventoryRecord';
import shallowCompare from '../../lib/shallowCompare';
import getThumbnailPhotoUrlFromHash from '../../lib/getThumbnailPhotoUrlFromHash';
import ShoppingCartActions from '../../actions/ShoppingCartActions';
import BookmarkActions from '../../actions/BookmarkActions';
import CustomerStore from '../../stores/CustomerStore';
import AttributedText from '../AttributedText';
import ZipCodeModal from '../ZipCodeModal';
import DelectableRating from '../DelectableRating';
import styles from './index.sass';
import HeartPng from './images/button_heart.png';
import RedHeartPng from './images/button_heart_highlight.png';
import HeartSvg from './images/button_heart.svg';
import RedHeartSvg from './images/button_heart_highlight.svg';

export const PhotoPositions = {
  Right: 'right',
  Left: 'left'
};
export const DetailsPositions = {
  Top: 'top',
  Middle: 'middle'
};
export const RemoveActions = {
  DeleteBookmark: BookmarkActions.delete
};
const ZipCodeProtectedActions = {
  AddToCart: 'addToCart',
  Bookmark: 'bookmark'
};

class InventoryRecordListElement extends React.Component {
  static propTypes = {
    inventoryRecord: React.PropTypes.instanceOf(InventoryRecord).isRequired,
    collectionSlug: React.PropTypes.string, // When provided, included in link href
    storeId: React.PropTypes.string.isRequired,
    bookmarkMetadata: React.PropTypes.object,
    photoPosition: React.PropTypes.string, // Dictates position of other elements as well
    detailsPosition: React.PropTypes.string,
    showFeaturedBadge: React.PropTypes.bool,
    showRatings: React.PropTypes.bool,
    showBuyButton: React.PropTypes.bool,
    showHeartButton: React.PropTypes.bool,
    showDescription: React.PropTypes.bool,
    showVariantPriceLine: React.PropTypes.bool,
    className: React.PropTypes.string,
    photoClassName: React.PropTypes.string,
    onClickRemoveAction: React.PropTypes.func
  };

  static defaultProps = {
    onClickRemoveAction: RemoveActions.DeleteBookmark,
    bookmarkMetadata: {},
    photoPosition: PhotoPositions.Right,
    detailsPosition: DetailsPositions.Top,
    showFeaturedBadge: true,
    showRatings: true,
    showBuyButton: true,
    showHeartButton: true,
    showDescription: true,
    showVariantPriceLine: false,
    className: '',
    photoClassName: ''
  };

  static contextTypes = {
    executeAction: React.PropTypes.func,
    getStore: React.PropTypes.func,
    getUserAgent: React.PropTypes.func
  };

  constructor(props) {
    super(props);

    this.state = {
      showZipCodeModal: false,
      attemptedAction: null
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  getCustomer() {
    return this.context.getStore(CustomerStore).getCurrentCustomer();
  }

  addToCart() {
    this.context.executeAction(ShoppingCartActions.addItem, {
      store_id: this.props.storeId,
      inventory_record_id: this.props.inventoryRecord.id,
      quantity: 1,
      inventoryRecord: this.props.inventoryRecord,
      navigateToBagOnSuccess: true
    });
  }

  bookmark() {
    let customerContext = this.props.inventoryRecord.customer_context;
    let bookmarked = customerContext ? customerContext.bookmarked : false;
    let action = !bookmarked ? BookmarkActions.create : BookmarkActions.delete;
    this.context.executeAction(action, _.extend({inventoryRecord: this.props.inventoryRecord}, this.props.bookmarkMetadata));
  }

  handleOnClickBuyButton(e) {
    let customer = this.getCustomer();
    if(!customer || !customer.preferences.zip_code) {
      this.setState({showZipCodeModal: true, attemptedAction: ZipCodeProtectedActions.AddToCart});
    } else {
      this.addToCart();
    }
  }

  handleOnClickHeartButton(e) {
    let customer = this.getCustomer();
    if(!customer || !customer.preferences.zip_code) {
      this.setState({showZipCodeModal: true, attemptedAction: ZipCodeProtectedActions.Bookmark});
    } else {
      this.bookmark();
    }
  }

  handleOnClickRemoveButton(e) {
    let payload;
    switch(this.props.onClickRemoveAction) {
      case RemoveActions.DeleteBookmark:
        payload = {inventoryRecord: this.props.inventoryRecord};
        break;
    }
    this.context.executeAction(this.props.onClickRemoveAction, payload);
  }

  handleOnCloseZipCodeModal(zipCodeAdded) {
    this.setState({showZipCodeModal: false});
    if(!zipCodeAdded) {return;}
    if(this.state.attemptedAction) {
      this[this.state.attemptedAction]();
      this.setState({attemptedAction: null});
    }
  }

  renderZipCodeModal() {
    if(!this.state.showZipCodeModal) {return null;}
    let zipCodeModalProps = {
      onClose: this.handleOnCloseZipCodeModal.bind(this)
    };
    return <ZipCodeModal {...zipCodeModalProps} />;
  }

  renderRatings(ratings=[]) {
    if(!this.props.showRatings) {return null;}
    // Inject below to test rating badge
    // ratings = [{
    //   type: 'delectable',
    //   object: {
    //     ratings_count: 62,
    //     average_rating: 9.3,
    //     pro_ratings_count: 12,
    //     average_pro_rating: 9.1
    //   }
    // }];
    let elems = [];
    ratings.forEach((rating, i) => {
      switch(rating.type) {
        case 'delectable':
          let delectableRatingProps = {
            key: i,
            rating: rating.object.average_rating,
            count: rating.object.ratings_count,
            style: {display: 'block', whiteSpace: 'pre'}
          };
          elems.push(<DelectableRating {...delectableRatingProps} />);
          break;
        default:
          break;
      }
    });

    if(!elems.length) {return null;}

    return (
      <div className={styles.ratings}>
        {elems}
      </div>
    );
  }

  renderJSONLD(inventoryRecord) {
    let json = inventoryRecord.generateSchemaJSON();
    return <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(json)}} />;
  }

  renderDescription(inventoryRecord, hideIfMobile) {
    if(!this.props.showDescription) {return null;}
    let { isMobile } = this.context.getUserAgent();
    if(hideIfMobile === isMobile) {return;}
    let descriptionLinkProps = {
      className: styles.description,
      routeName: this.props.collectionSlug ? 'collectionInventoryRecord' : 'inventoryRecord',
      navParams: {
        id: inventoryRecord.id,
        slug: inventoryRecord.slug,
        collectionSlug: this.props.collectionSlug
      }
    };
    let text = inventoryRecord.description;
    if(text && text.length > 160) {
      text = text.slice(0, 160);// + ' …';
    }
    if(descriptionLinkProps.routeName) {
      return <NavLink {...descriptionLinkProps}>{text}</NavLink>;
    }
    return <p {...descriptionLinkProps}>{text}</p>;
  }

  renderBuyButton() {
    if(!this.props.showBuyButton) {return null;}
    let buyButtonProps = {
      className: styles.buyButton,
      onClick: this.handleOnClickBuyButton.bind(this),
    };
    let priceText = this.props.inventoryRecord.price_text;
    let salePriceText = this.props.inventoryRecord.sale_price_text;

    let availability = this.props.inventoryRecord.availability;

    if(availability === 'sold_out') {
      buyButtonProps.disabled = true;
      return <button {...buyButtonProps}>Sold out</button>;
    } else if(availability === 'unavailable') {
      buyButtonProps.disabled = true;
      return <button {...buyButtonProps}>Unavailable</button>;
    } else if(salePriceText) {
      return (
        <button {...buyButtonProps}>
          <span className={styles.strikethrough}>{priceText}</span>
          <span>{salePriceText}</span>
        </button>
      );
    }
    return <button {...buyButtonProps}>{priceText}</button>;
  }

  renderHeartButton() {
    if(!this.props.showHeartButton) {return null;}
    let customerContext = this.props.inventoryRecord.customer_context;
    let bookmarked = customerContext ? customerContext.bookmarked : false;
    let heartButtonProps = {
      className: styles.heartButton,
      onClick: this.handleOnClickHeartButton.bind(this)
    };
    return (
      <button {...heartButtonProps}>
        <img src={bookmarked ? RedHeartPng : HeartPng} srcSet={bookmarked ? RedHeartSvg : HeartSvg} />
      </button>
    );
  }

  renderVariantPriceLine() {
    if(!this.props.showVariantPriceLine) {return null;}
    return (
      <div className={styles.variantPriceLine}>
        <span className={styles.price}>{this.props.inventoryRecord.price_text}</span>
        <span>&nbsp;•&nbsp;</span>
        <button className={styles.removeButton} onClick={this.handleOnClickRemoveButton.bind(this)}>Remove</button>
      </div>
    );
  }

  renderName(inventoryRecord) {
    let nameLinkProps = {
      className: styles.name,
      routeName: this.props.collectionSlug ? 'collectionInventoryRecord' : 'inventoryRecord',
      navParams: {
        id: inventoryRecord.id,
        slug: inventoryRecord.slug,
        collectionSlug: this.props.collectionSlug
      }
    };
    if(nameLinkProps.routeName) {
      return <NavLink {...nameLinkProps}>{inventoryRecord.brand} {inventoryRecord.name}</NavLink>;
    }
    return <p {...nameLinkProps}>{inventoryRecord.brand} {inventoryRecord.name}</p>;
  }

  renderPhoto(inventoryRecord) {
    let photoUrl = getThumbnailPhotoUrlFromHash(inventoryRecord.photo_hash, 300);
    let photoLinkProps = {
      className: `${styles.photo} ${this.props.photoClassName}`,
      routeName: this.props.collectionSlug ? 'collectionInventoryRecord' : 'inventoryRecord',
      navParams: {
        id: inventoryRecord.id,
        slug: inventoryRecord.slug,
        collectionSlug: this.props.collectionSlug
      }
    };
    if(photoLinkProps.routeName) {
      return (
        <NavLink {...photoLinkProps}>
          <span content={photoUrl} />
          <img src={photoUrl} />
        </NavLink>
      );
    }
    return (
      <div {...photoLinkProps}>
        <span content={photoUrl} />
        <img src={photoUrl} />
      </div>
    );
  }

  render() {
    if(!this.props || !this.props.inventoryRecord) {return null;}
    let inventoryRecord = this.props.inventoryRecord;
    let classes = `${styles.inventoryRecordListElement} ${styles[this.props.photoPosition]}`;

    if(this.props.className) {
      classes += ` ${this.props.className}`;
    }

    let featuredBadge;
    if(this.props.showFeaturedBadge && inventoryRecord.featured) {
      featuredBadge = <span className={styles.featuredBadge}>Staff Pick</span>;
    }

    return (
      <div className={classes}>
        {this.renderPhoto(inventoryRecord)}
        <div className={`${styles.details} ${styles[this.props.detailsPosition]}`}>
          {featuredBadge}
          {this.renderName(inventoryRecord)}
          {this.renderRatings(inventoryRecord.ratings)}
          {this.renderDescription(inventoryRecord, true)}
          {this.renderVariantPriceLine()}
        </div>
        <div className={styles.buttonContainer}>
          {this.renderBuyButton()}
          {this.renderHeartButton()}
        </div>
        {this.renderDescription(inventoryRecord, false)}
        {this.renderZipCodeModal()}
        {this.renderJSONLD(inventoryRecord)}
      </div>
    );
  }
}

export default InventoryRecordListElement;



// WEBPACK FOOTER //
// banquet/components/InventoryRecordListElement/index.js